import React, { useEffect, useState, useCallback } from 'react';
import { Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import { HostPrimaryButton, HostSettingsCard, HostToggleRow } from '../../components/HostSettingsCard';
import { SignInMethodCard } from '../../components/SignInMethodCard';
import { BiometricUnlockCard } from '../../components/BiometricUnlockCard';
import { fetchAccountSettings, updateAccountSecurity } from '../../utils/accountSettingsApi';

const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 14, border: '1px solid var(--card-border)', background: 'var(--surface)', fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)', outline: 'none' };

export default function HostLoginSecurityPage() {
  const [email, setEmail] = useState('');
  const [security, setSecurity] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [toggles, setToggles] = useState({ login_alerts: true, two_factor_enabled: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const settings = await fetchAccountSettings();
      setEmail(settings.security.email || '');
      setSecurity(settings.security);
      setToggles({
        login_alerts: settings.security.login_alerts,
        two_factor_enabled: settings.security.two_factor_enabled,
      });
    } catch (err) {
      toast.error('Failed to load security settings');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New password and confirmation must match');
      return;
    }

    setSaving(true);
    try {
      await updateAccountSecurity({
        current_password: currentPassword,
        new_password: newPassword || undefined,
        login_alerts: toggles.login_alerts,
        two_factor_enabled: toggles.two_factor_enabled,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Login & security updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update security settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HostSettingsLayout title="Login & Security" dataTestId="host-login-security-page">
      <SignInMethodCard security={security} onChanged={load} />
      <BiometricUnlockCard />
      <HostSettingsCard data-testid="host-security-email-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Mail size={24} color="var(--text)" />
          <div>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, color: 'var(--text)', margin: 0 }}>Email Address</p>
            <p data-testid="host-security-email-value" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: '#666', margin: '4px 0 0' }}>{email}</p>
          </div>
        </div>
      </HostSettingsCard>

      <HostSettingsCard data-testid="host-password-card">
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: '0 0 14px' }}>Change Password</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input type="password" placeholder="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="host-current-password-input" style={inputStyle} />
          <input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="host-new-password-input" style={inputStyle} />
          <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="host-confirm-password-input" style={inputStyle} />
        </div>
      </HostSettingsCard>

      <HostSettingsCard data-testid="host-security-preferences-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <ShieldCheck size={24} color="var(--text)" />
          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: 0 }}>Security Preferences</p>
        </div>
        <HostToggleRow label="Login Alerts" description="Get alerted when your account is accessed from a new device." checked={toggles.login_alerts} onChange={() => setToggles(prev => ({ ...prev, login_alerts: !prev.login_alerts }))} dataTestId="host-login-alerts-toggle" />
        <HostToggleRow label="Two-Step Verification" description="Add another layer of protection to your host account." checked={toggles.two_factor_enabled} onChange={() => setToggles(prev => ({ ...prev, two_factor_enabled: !prev.two_factor_enabled }))} dataTestId="host-two-factor-toggle" />
      </HostSettingsCard>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
        <HostPrimaryButton onClick={handleSave} dataTestId="host-security-save-button" disabled={saving}>{saving ? 'Saving...' : 'DONE'}</HostPrimaryButton>
      </div>
    </HostSettingsLayout>
  );
}